import { useQuery } from '@tanstack/react-query';
import { getSurveyResponses, getSurveys } from '../../api/client';
import { formatDateTime } from '../../utils/formatters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { COLORS } from '../../utils/constants';

interface SurveyResponsesProps {
  surveyId: string;
}

export default function SurveyResponses({ surveyId }: SurveyResponsesProps) {
  const { data: surveys = [] } = useQuery({
    queryKey: ['surveys'],
    queryFn: getSurveys,
  });

  const survey = surveys.find((s) => s.id === surveyId);

  const { data: responses = [], isLoading } = useQuery({
    queryKey: ['survey-responses', surveyId],
    queryFn: () => getSurveyResponses(surveyId),
  });

  if (isLoading) {
    return (
      <div className="card flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  // Group responses by question
  const questionStats = (survey?.questions || []).map((question) => {
    const answers = (responses || [])
      .flatMap((r) => r.answers || [])
      .filter((a) => a.questionId === question.id);

    if (question.type === 'rating' || question.type === 'nps') {
      const values = answers.map((a) => Number(a.value));
      const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
      const max = question.type === 'nps' ? 10 : 5;

      const distribution = Array.from({ length: max + 1 }, (_, i) => ({
        label: String(question.type === 'nps' ? i : i + 1),
        count: values.filter((v) => v === (question.type === 'nps' ? i : i + 1)).length,
      })).filter((d) => question.type === 'rating' ? d.label !== '0' : true);

      return { question, avg, distribution, answers };
    }

    if (question.type === 'multiple_choice') {
      const counts: Record<string, number> = {};
      answers.forEach((a) => {
        const val = String(a.value);
        counts[val] = (counts[val] || 0) + 1;
      });
      const distribution = Object.entries(counts).map(([label, count]) => ({ label, count }));
      return { question, distribution, answers };
    }

    // Text responses
    return { question, answers };
  }) || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">{survey?.name} - Responses</h2>
        <p className="text-sm text-gray-500">{responses.length} total responses</p>
      </div>

      {questionStats.map((stat, i) => (
        <div key={i} className="card">
          <h3 className="text-sm font-semibold text-gray-900">
            Q{i + 1}: {stat.question.text}
          </h3>
          <p className="text-xs text-gray-500 capitalize">{stat.question.type} question</p>

          {/* Rating / NPS Chart */}
          {(stat.question.type === 'rating' || stat.question.type === 'nps') &&
            stat.distribution && (
              <div className="mt-4">
                <p className="mb-2 text-sm text-gray-500">
                  Average:{' '}
                  <span className="font-semibold text-gray-900">
                    {'avg' in stat ? stat.avg?.toFixed(1) : 'N/A'}
                  </span>
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stat.distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                    <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

          {/* Multiple Choice Chart */}
          {stat.question.type === 'multiple_choice' && stat.distribution && (
            <div className="mt-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stat.distribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9ca3af" allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fontSize: 12 }}
                    stroke="#9ca3af"
                    width={120}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <Bar dataKey="count" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Text Responses */}
          {stat.question.type === 'text' && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
              {stat.answers.length === 0 ? (
                <p className="text-sm text-gray-500">No text responses yet</p>
              ) : (
                stat.answers.map((answer, ai) => (
                  <div
                    key={ai}
                    className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700"
                  >
                    {String(answer.value)}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      ))}

      {/* Raw Responses Table */}
      <div className="card">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">All Responses</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="table-header">Session</th>
                <th className="table-header">Submitted</th>
                {(survey?.questions || []).map((q, i) => (
                  <th key={i} className="table-header">
                    Q{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {responses.slice(0, 50).map((response) => (
                <tr key={response.id} className="hover:bg-gray-50">
                  <td className="table-cell font-mono text-xs">
                    {response.sessionId.slice(0, 8)}
                  </td>
                  <td className="table-cell text-gray-500">
                    {formatDateTime(response.submittedAt)}
                  </td>
                  {(survey?.questions || []).map((q, qi) => {
                    const answer = response.answers.find((a) => a.questionId === q.id);
                    return (
                      <td key={qi} className="table-cell max-w-[200px] truncate">
                        {answer ? String(answer.value) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {responses.length === 0 && (
                <tr>
                  <td
                    colSpan={(survey?.questions.length || 0) + 2}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No responses yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
